import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Profile = () => {
  const { t } = useTranslation();
  const { user, token, isAuthenticated, updateUser, API } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        greeting: user.greeting || '',
        academic_title: user.academic_title || '',
        primary_affiliation: user.primary_affiliation || '',
        primary_position: user.primary_position || '',
        secondary_affiliation: user.secondary_affiliation || '',
        secondary_position: user.secondary_position || '',
        tertiary_affiliation: user.tertiary_affiliation || '',
        tertiary_position: user.tertiary_position || '',
        bio: user.bio || '',
        phone: user.phone || '',
        country: user.country || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.put(`${API}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUser(res.data);
      setMsg(t('profile.updated'));
      setEditing(false);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to update profile');
    }
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="profile-login-required" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <h2>{t('auth.signIn')}</h2>
        <p>{t('workspace.loginRequiredDesc')}</p>
      </div>
    );
  }

  const displayName = `${user.greeting ? user.greeting + ' ' : ''}${user.academic_title ? user.academic_title + ' ' : ''}${user.name}`;

  return (
    <div data-testid="profile-page" style={{ maxWidth: '700px', margin: '0 auto', padding: '1rem 0' }}>
      <h1 style={{ marginBottom: '0.5rem' }}>{t('profile.title')}</h1>
      <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>{t('profile.subtitle')}</p>

      {msg && <div className="success-message" data-testid="profile-msg">{msg}</div>}

      {!editing ? (
        <div data-testid="profile-view" className="card" style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div>
              <h2 style={{ marginBottom: '0.25rem' }}>{displayName}</h2>
              <p style={{ color: 'var(--muted)' }}>{user.email}</p>
              <span className={`role-badge ${user.role === 'admin' ? 'role-admin' : 'role-member'}`}>{user.role || 'member'}</span>
            </div>
            <button className="btn" onClick={() => setEditing(true)} data-testid="edit-profile-btn">{t('profile.editProfile')}</button>
          </div>

          <div className="profile-grid">
            <ProfileField label={t('profile.phone')} value={user.phone} />
            <ProfileField label={t('profile.country')} value={user.country} />
            <ProfileField label={t('auth.primaryAffiliation')} value={user.primary_affiliation} />
            <ProfileField label={t('auth.position')} value={user.primary_position} />
            <ProfileField label={t('auth.secondaryAffiliation')} value={user.secondary_affiliation} />
            <ProfileField label={t('auth.position')} value={user.secondary_position} />
            <ProfileField label={t('auth.tertiaryAffiliation')} value={user.tertiary_affiliation} />
            <ProfileField label={t('auth.position')} value={user.tertiary_position} />
          </div>

          {user.bio && (
            <div style={{ marginTop: '1.5rem' }}>
              <strong style={{ color: 'var(--brand)' }}>{t('profile.bio')}</strong>
              <p style={{ marginTop: '0.5rem', lineHeight: '1.6' }}>{user.bio}</p>
            </div>
          )}

          <p style={{ marginTop: '1.5rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
            {t('common.memberSince')} {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSave} data-testid="profile-edit-form" className="card" style={{ padding: '2rem' }}>
          <h3 style={{ color: 'var(--brand)', marginBottom: '1rem' }}>{t('profile.personalInfo')}</h3>
          <div className="form-row">
            <div className="form-group" style={{ flex: '0 0 30%' }}>
              <label>{t('auth.greeting')}</label>
              <select name="greeting" value={formData.greeting} onChange={handleChange} data-testid="profile-greeting">
                <option value="">Select...</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Non disclosed">Non disclosed</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: '0 0 65%' }}>
              <label>{t('auth.academicTitle')}</label>
              <select name="academic_title" value={formData.academic_title} onChange={handleChange} data-testid="profile-academic-title">
                <option value="">Select...</option>
                <option value="Dr">Dr</option>
                <option value="Prof">Prof</option>
                <option value="Prof Dr">Prof Dr</option>
                <option value="Non disclosed">Non disclosed</option>
                <option value="Others">Others</option>
              </select>
            </div>
          </div>
          <div className="form-group"><label>{t('auth.fullName')}</label><input name="name" value={formData.name} onChange={handleChange} required data-testid="profile-name" /></div>
          <div className="form-row">
            <div className="form-group"><label>{t('profile.phone')}</label><input name="phone" value={formData.phone} onChange={handleChange} data-testid="profile-phone" /></div>
            <div className="form-group"><label>{t('profile.country')}</label><input name="country" value={formData.country} onChange={handleChange} data-testid="profile-country" /></div>
          </div>
          <div className="form-group"><label>{t('profile.bio')}</label><textarea name="bio" value={formData.bio} onChange={handleChange} rows="3" data-testid="profile-bio" placeholder={t('profile.bioPlaceholder')} /></div>

          <h3 style={{ color: 'var(--brand)', marginTop: '1.5rem', marginBottom: '1rem' }}>{t('profile.affiliations')}</h3>
          <div className="form-group"><label>{t('auth.primaryAffiliation')}</label><input name="primary_affiliation" value={formData.primary_affiliation} onChange={handleChange} data-testid="profile-primary-affiliation" /></div>
          <div className="form-group"><label>{t('auth.position')}</label><input name="primary_position" value={formData.primary_position} onChange={handleChange} data-testid="profile-primary-position" /></div>
          <div className="form-group"><label>{t('auth.secondaryAffiliation')}</label><input name="secondary_affiliation" value={formData.secondary_affiliation} onChange={handleChange} data-testid="profile-secondary-affiliation" /></div>
          <div className="form-group"><label>{t('auth.position')}</label><input name="secondary_position" value={formData.secondary_position} onChange={handleChange} data-testid="profile-secondary-position" /></div>
          <div className="form-group"><label>{t('auth.tertiaryAffiliation')}</label><input name="tertiary_affiliation" value={formData.tertiary_affiliation} onChange={handleChange} data-testid="profile-tertiary-affiliation" /></div>
          <div className="form-group"><label>{t('auth.position')}</label><input name="tertiary_position" value={formData.tertiary_position} onChange={handleChange} data-testid="profile-tertiary-position" /></div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="submit" className="btn" disabled={loading} data-testid="save-profile-btn">{loading ? t('profile.saving') : t('profile.saveChanges')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">{t('common.cancel')}</button>
          </div>
        </form>
      )}
    </div>
  );
};

const ProfileField = ({ label, value }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <strong style={{ color: 'var(--brand)', fontSize: '0.85rem' }}>{label}</strong>
    <p style={{ margin: '0.2rem 0 0' }}>{value || '-'}</p>
  </div>
);

export default Profile;
